import React, { useState } from "react";
import cn from "classnames";
import dayjs from "dayjs";
import { useQuery } from "@apollo/client";
import { GET_USER_APPS } from "../../../api/graphql/users";
import DataTable, { DataColumn } from "../../DataTable";
import { getStoreIcon } from "../../Campaign";
import Star from "../../../assets/star-rating.svg";
import { AppData } from "./types";
import styles from "./AppsTab.module.scss";

type UserApp = {
  appId: string;
  title?: string | null;
  icon?: string | null;
  store: string;
  country?: string | null;
  status?: string | null;
  score?: number | null;
  reviewsCount?: number | null;
  version?: string | null;
  updated?: string | null;
  size?: string | null;
  keywordsCount?: number | null;
  totalSpend?: number | null;
  activeOrdersCount?: number | null;
};

interface AppsTabProps {
  userId: number;
  /** The apps the user page already has; shown when the full list fails. */
  apps: AppData[];
}

export const calculateSize = (size: number | string) => {
  const mb = Number(size) / 1000000;

  if (mb === 0) return "-";
  if (mb < 0.1) return "-";

  return `${mb?.toFixed(1)}`;
};

const regionNames =
  typeof Intl.DisplayNames === "function"
    ? new Intl.DisplayNames(["en"], { type: "region" })
    : null;

/** "ua" → "Ukraine", as My Apps names countries. */
const countryName = (code?: string | null) => {
  if (!code) return "-";
  try {
    return regionNames?.of(code.toUpperCase()) ?? code.toUpperCase();
  } catch {
    return code.toUpperCase();
  }
};

/** My Apps' count format: 2.6, 13.4K, 1.2M. */
const formatBigNumber = (value: number) => {
  if (value < 1e3) return value.toFixed(1);
  if (value < 1e6) return `${+(value / 1e3).toFixed(1)}K`;
  if (value < 1e9) return `${+(value / 1e6).toFixed(1)}M`;
  return value.toFixed(1);
};

// The availability checks write UNAVAILABLE or BANNED; an empty status is an
// app they have not reached yet, not a broken one.
const isUnavailable = (status?: string | null) =>
  ["UNAVAILABLE", "BANNED"].includes(String(status ?? "").toUpperCase());

const AppIcon: React.FC<{ icon?: string | null; store: string }> = ({
  icon,
  store,
}) => {
  const [failed, setFailed] = useState(false);
  const url = typeof icon === "string" ? icon.trim() : "";
  return (
    <span className={styles.appIcon}>
      {url && !failed ? (
        <img
          src={url}
          alt=""
          referrerPolicy="no-referrer"
          onError={() => setFailed(true)}
        />
      ) : (
        getStoreIcon(store)
      )}
    </span>
  );
};

const byNumber =
  (pick: (app: UserApp) => unknown) => (a: UserApp, b: UserApp) =>
    (Number(pick(a)) || 0) - (Number(pick(b)) || 0);

const columns: DataColumn<UserApp>[] = [
  {
    key: "title",
    title: "App",
    width: 240,
    fixed: true,
    mobile: "title",
    render: (app) => (
      <span className={styles.appCell}>
        <AppIcon icon={app.icon} store={app.store} />
        <span className={styles.appInfo}>
          <span className={styles.appName} title={app.title || app.appId}>
            {app.title || app.appId}
          </span>
          <span
            className={cn(
              styles.appStatus,
              isUnavailable(app.status) ? styles.unavailable : styles.available,
            )}
          >
            {isUnavailable(app.status) ? "Unavailable" : "Available"}
          </span>
        </span>
      </span>
    ),
    sorter: (a, b) => (a.title || "").localeCompare(b.title || ""),
  },
  {
    key: "store",
    title: "Store",
    width: 84,
    align: "center",
    render: (app) => (
      <span className={styles.storeBadge}>{getStoreIcon(app.store)}</span>
    ),
    sorter: (a, b) => a.store.localeCompare(b.store),
  },
  {
    key: "country",
    title: "Country",
    width: 132,
    render: (app) => countryName(app.country),
    sorter: (a, b) =>
      countryName(a.country).localeCompare(countryName(b.country)),
  },
  {
    key: "ratings",
    title: "Ratings",
    width: 100,
    render: (app) => (
      <span className={styles.ratingsCell}>
        <img className={styles.starIcon} src={Star} alt="" />
        {(Number(app.score) || 0).toFixed(1)}
      </span>
    ),
    sorter: byNumber((app) => app.score),
  },
  {
    key: "reviews",
    title: "Reviews",
    width: 104,
    render: (app) => (
      <span className={styles.ratingsCell}>
        <img className={styles.starIcon} src={Star} alt="" />
        {formatBigNumber(Number(app.reviewsCount) || 0)}
      </span>
    ),
    sorter: byNumber((app) => app.reviewsCount),
  },
  {
    key: "updated",
    title: "Last update",
    width: 132,
    render: (app) =>
      app.updated ? dayjs(app.updated).format("DD.MM.YYYY") : "-",
    sorter: (a, b) =>
      dayjs(a.updated || 0).valueOf() - dayjs(b.updated || 0).valueOf(),
  },
  {
    key: "keywordsCount",
    title: "Keywords",
    width: 96,
    render: (app) => Number(app.keywordsCount) || 0,
    sorter: byNumber((app) => app.keywordsCount),
  },
  {
    key: "totalSpend",
    title: "Total spend",
    width: 112,
    render: (app) => `$ ${(Number(app.totalSpend) || 0).toFixed(2)}`,
    sorter: byNumber((app) => app.totalSpend),
  },
  {
    key: "activeOrdersCount",
    title: "Active orders",
    width: 120,
    render: (app) => Number(app.activeOrdersCount) || 0,
    sorter: byNumber((app) => app.activeOrdersCount),
  },
  {
    key: "version",
    title: "Version",
    width: 100,
    render: (app) => app.version || "-",
  },
  {
    key: "size",
    title: "Size (Mb)",
    width: 100,
    render: (app) => (app.size ? calculateSize(app.size) : "-"),
    sorter: byNumber((app) => app.size),
  },
];

/**
 * The user's apps as their My Apps shows them: ratings, reviews, last
 * update, keywords, spend and active orders, in the DS table.
 */
const AppsTab: React.FC<AppsTabProps> = ({ userId, apps: fallback }) => {
  const { data, loading, error } = useQuery<{ getUserApps: UserApp[] }>(
    GET_USER_APPS,
    { variables: { userId }, fetchPolicy: "network-only" },
  );

  if (loading) return <p>Loading…</p>;

  // A backend without the query: the names the user page already has.
  const apps: UserApp[] = error
    ? fallback.map((app) => ({
        appId: app.appId ?? "",
        title: app.title,
        store: app.store,
        country: app.country,
      }))
    : (data?.getUserApps ?? []);

  if (apps.length === 0) return <p>No apps associated with this user.</p>;

  return (
    <div className={styles.wrap}>
      <DataTable
        id="user-apps"
        columns={columns}
        rows={apps}
        rowKey={(app) => `${app.appId}-${app.store}-${app.country}`}
        emptyText="No apps associated with this user."
      />
    </div>
  );
};

export default AppsTab;
